import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Stack, Typography, Paper, Tabs, Tab, TextField,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Add as AddIcon, Delete as DeleteIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';

import { Container } from '@/components/common';
import { useCreateQuestion } from '@/hooks/aiReview.hook';
import { successToast, errorToast } from '@/common/toast';
import { DEFAULT_PROMPTS, LANGUAGE_OPTIONS } from '@/constants/trainings';
import { getExtensionFromLanguage } from './utils';
import { FileExplorer, ProjectCodeViewer } from './components';

/**
 * 프로젝트 개발훈련 등록 페이지
 */
const ProjectAddPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const languageId = searchParams.get('languageId');
  const [promptTab, setPromptTab] = useState(0);
  const [activeFileIdx, setActiveFileIdx] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    languageId: null,
    prompts: DEFAULT_PROMPTS.map((p) => ({ ...p })),
    files: [],
  });

  const createQuestionMutation = useCreateQuestion();

  const files = formData.files ?? [];
  const prompts = formData.prompts ?? [];
  const activeFile = useMemo(
    () => (activeFileIdx != null && files[activeFileIdx] ? files[activeFileIdx] : files[0]),
    [files, activeFileIdx]
  );

  useEffect(() => {
    if (files.length > 0 && activeFileIdx === null) {
      setActiveFileIdx(0);
    }
  }, [files.length, activeFileIdx]);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updatePrompt = useCallback((index, field, value) => {
    setFormData((prev) => {
      const list = [...(prev.prompts ?? [])];
      if (list[index]) {
        list[index] = { ...list[index], [field]: value };
      }
      return { ...prev, prompts: list };
    });
  }, []);

  const addFile = useCallback(() => {
    const isFirstFile = files.length === 0;
    const ext = isFirstFile ? 'md' : getExtensionFromLanguage(formData.languageId);
    const newFile = {
      id: `new-${Date.now()}`,
      fileName: isFirstFile ? 'README.md' : `파일${(files.length + 1).toString()}.${ext}`,
      content: '',
      isReadonly: !isFirstFile,
    };
    setFormData((prev) => ({
      ...prev,
      files: [...(prev.files ?? []), newFile],
    }));
    setActiveFileIdx(files.length);
  }, [files.length, formData.languageId]);

  const removeFile = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
    setActiveFileIdx((prev) => {
      const nextLength = files.length - 1;
      if (nextLength <= 0) return null;
      if (prev === index) return 0;
      if (prev > index) return prev - 1;
      return prev;
    });
  }, [files.length]);

  const updateFile = useCallback((index, field, value) => {
    setFormData((prev) => {
      const list = [...(prev.files ?? [])];
      if (list[index]) {
        const key = field === 'content' ? 'content' : field === 'isReadonly' ? 'isReadonly' : field;
        list[index] = { ...list[index], [key]: value };
      }
      return { ...prev, files: list };
    });
  }, []);

  const updateFileContent = useCallback((index, content) => {
    updateFile(index, 'content', content ?? '');
  }, [updateFile]);

  const handleEditableChange = useCallback((index, isEditable) => {
    if (isEditable) {
      setFormData((prev) => ({
        ...prev,
        files: (prev.files ?? []).map((f, i) => ({
          ...f,
          isReadonly: i !== index,
        })),
      }));
    } else {
      setFormData((prev) => {
        const list = [...(prev.files ?? [])];
        if (list[index]) list[index] = { ...list[index], isReadonly: true };
        return { ...prev, files: list };
      });
    }
  }, []);

  const handleApplyFirstToSecond = useCallback(() => {
    setFormData((prev) => {
      const first = prev.prompts?.[0];
      if (!first) return prev;
      const list = [...(prev.prompts ?? [])];
      if (list[1]) {
        list[1] = {
          ...list[1],
          model: first.model ?? '',
          temperature: first.temperature ?? '',
          maxTokens: first.maxTokens ?? '',
          judgmentCriteria: first.judgmentCriteria ?? '',
          fewShotExamples: first.fewShotExamples ?? '',
        };
      }
      return { ...prev, prompts: list };
    });
    successToast('1차 프롬프트 내용이 2차 프롬프트에도 적용되었습니다.');
  }, []);

  const onSubmit = useCallback(async () => {
    if (!formData.title?.trim()) {
      errorToast('제목을 입력해주세요.');
      return;
    }
    const invalidPrompt = formData.prompts?.find(
      (p) => !p.judgmentCriteria?.trim() || !p.fewShotExamples?.trim()
    );
    if (invalidPrompt) {
      errorToast('모든 프롬프트에 판단 기준과 Few-shot 예시를 입력해주세요.');
      return;
    }

    const invalidFiles = formData.files?.filter(f => f.isReadonly === false).filter((f) => !f.content?.includes('[작성 영역 시작]') || !f.content?.includes('[작성 영역 끝]'));
    if (invalidFiles.length > 0) {
      errorToast('파일 내용에 "[작성 영역 시작]"과 "[작성 영역 끝]"이 한 번 씩 반드시 존재해야 합니다.');
      return;
    }

    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description?.trim() ?? '',
        languageId: formData.languageId,
        prompts: formData.prompts.map((p) => ({
          attemptOrder: p.attemptOrder,
          model: p.model ?? null,
          judgmentCriteria: p.judgmentCriteria ?? '',
          fewShotExamples: p.fewShotExamples ?? '',
          temperature: p.temperature != null ? Number(p.temperature) : null,
          maxTokens: p.maxTokens != null ? Number(p.maxTokens) : null,
        })),
        files: formData.files.map((f) => ({
          fileName: f.fileName,
          content: f.content ?? '',
          isReadonly: f.isReadonly ?? false,
        })),
      };
      await createQuestionMutation.mutateAsync(payload);
      successToast('프로젝트가 등록되었습니다.');
      const query = languageId ? `?languageId=${languageId}` : '';
      navigate(`/education/projects${query}`);
    } catch (err) {
      errorToast(`프로젝트 등록에 실패했습니다. - ${err.response?.data?.msg}`);
    }
  }, [formData, createQuestionMutation, navigate, languageId]);

  return (
    <Container title="프로젝트 등록">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            const query = languageId ? `?languageId=${languageId}` : '';
            navigate(`/education/projects${query}`);
          }}
        >
          목록으로
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSubmit}
          disabled={createQuestionMutation.isPending}
        >
          등록
        </Button>
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          기본 정보
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="제목"
            fullWidth
            required
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
          />
          <TextField
            label="설명"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
          />
          {/* 언어 타입 선택을 위한 Select 컴포넌트 (styled-components 예시는 아래 주석) */}
          <FormControl fullWidth required>
            <InputLabel id="language-select-label">언어 타입</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              label="언어 타입"
              value={formData.languageId ?? ''}
              onChange={(e) => updateField('languageId', e.target.value)}
            >
              <MenuItem value="">
                <em>선택 없음</em>
              </MenuItem>
              {/* LANGUAGE_OPTIONS에서 언어 목록을 메뉴로 변환 */}
              {LANGUAGE_OPTIONS.map((lang) => (
                <MenuItem key={lang.languageId} value={lang.languageId}>
                  {lang.languageName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Tabs value={promptTab} onChange={(_, v) => setPromptTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {prompts.map((_, idx) => (
            <Tab key={idx} label={`${idx + 1}차 프롬프트`} />
          ))}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              disabled={prompts[0]?.judgmentCriteria?.trim() === prompts[1]?.judgmentCriteria?.trim() && prompts[0]?.fewShotExamples?.trim() === prompts[1]?.fewShotExamples?.trim()}
              onClick={handleApplyFirstToSecond}
            >
              1차 프롬프트 내용을 2차 프롬프트에도 적용
            </Button>
        </Tabs>
        {prompts.map((prompt, idx) => (
          <Box key={idx} role="tabpanel" hidden={promptTab !== idx} sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" gap={2} flexWrap="wrap">
                <TextField
                  label="모델"
                  size="small"
                  value={prompt.model ?? ''}
                  onChange={(e) => updatePrompt(idx, 'model', e.target.value)}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="Temperature"
                  type="number"
                  size="small"
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  value={prompt.temperature ?? ''}
                  onChange={(e) => updatePrompt(idx, 'temperature', parseFloat(e.target.value) ?? '')}
                  sx={{ minWidth: 100 }}
                />
                <TextField
                  label="Max Tokens"
                  type="number"
                  size="small"
                  value={prompt.maxTokens ?? ''}
                  onChange={(e) => updatePrompt(idx, 'maxTokens', parseInt(e.target.value, 10) || '')}
                  sx={{ minWidth: 100 }}
                />
              </Stack>
              <TextField
                label="판단 기준 (필수)"
                fullWidth
                multiline
                rows={8}
                required
                value={prompt.judgmentCriteria ?? ''}
                onChange={(e) => updatePrompt(idx, 'judgmentCriteria', e.target.value)}
                placeholder="판단 기준을 입력하세요."
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'Consolas, "Courier New", monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
              <TextField
                label="Few-shot 예시 (필수)"
                fullWidth
                multiline
                rows={15}
                required
                value={prompt.fewShotExamples ?? ''}
                onChange={(e) => updatePrompt(idx, 'fewShotExamples', e.target.value)}
                placeholder="Few-shot 예시를 입력하세요."
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'Consolas, "Courier New", monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Stack>
          </Box>
        ))}
      </Paper>

      <Paper sx={{ overflow: 'hidden', mb: 2 }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            파일
          </Typography>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addFile}>
            파일 추가
          </Button>
        </Box>
      </Paper>

      {files.length > 0 ? (
        <FileExplorer
          files={files}
          activeKey={activeFileIdx}
          selectionMode="index"
          mode="edit"
          headerTitle="파일"
          onSelectFile={setActiveFileIdx}
          onRemoveFile={removeFile}
          onEditableChange={handleEditableChange}
        >
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="파일명"
              value={activeFile?.fileName ?? ''}
              onChange={(e) => updateFile(activeFileIdx ?? 0, 'fileName', e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={activeFile ? !activeFile.isReadonly : false}
                  onChange={(e) => {
                    if (activeFileIdx == null) return;
                    handleEditableChange(activeFileIdx, e.target.checked);
                  }}
                />
              }
              label="수정 가능"
              labelPlacement="start"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.813rem' } }}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 600 }}>
            <ProjectCodeViewer
              key={activeFile?.id ?? activeFileIdx}
              fileName={activeFile?.fileName ?? ''}
              content={activeFile?.content ?? ''}
              readOnly={false}
              onChange={(value) => updateFileContent(activeFileIdx ?? 0, value ?? '')}
            />
          </Box>
        </FileExplorer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            등록된 파일이 없습니다. &quot;파일 추가&quot; 버튼으로 파일을 추가하세요.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addFile}>
            파일 추가
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default ProjectAddPage;
